import { useEffect, useRef } from 'react';

type Event = MouseEvent | TouchEvent;
interface UseClickOutsideOptions {
  excludeSelector?: string;
}

const useClickOutside = <T extends HTMLElement>(
  callback: () => void,
  options: UseClickOutsideOptions = {}
): React.RefObject<T> => {
  const { excludeSelector } = options;
  const ref = useRef<T>(null);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (!ref.current) return;

      if (excludeSelector) {
        const excludedElements = document.querySelectorAll(excludeSelector);
        const clickedOnExcluded = Array.from(excludedElements).some((element) =>
          element.contains(event.target as Node)
        );

        if (clickedOnExcluded) return;
      }

      if (!ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [callback, excludeSelector]);

  return ref;
};

export default useClickOutside;
